import { BankActItemCfg } from '@/types/merchant-info';
import { Ref, ref, toRefs, watchEffect } from 'vue';
import {
  CouponActInfoWithGoodsAndState,
  CouponActRelation,
  DistributionActivity,
} from '@tencent/wphk-act-base-framework';
import { COUPON_SORT_STATE } from '../config';
import { useMerchantInfo } from '@/store//merchant-info-module';
import { getServerTime } from '@tencent/wphk-components-common/utils/server-time';
import dayjs from 'dayjs';
import { to } from '@tencent/wphk-base-utils';
import { elk, CustomException } from '@tencent/ppd-base-libs';

export function useBankAct() {
  // ---- hooks start ----
  const { merchantConfig } = toRefs(useMerchantInfo());
  // ---- hooks end ----
  // ---- 变量start ----
  // 是否隐藏bank act模块，如果没有券或者查券失败，则隐藏模块
  const isHideModule = ref(false);
  // 券内容是否可以开始渲染
  const isReady = ref(false);
  // 券面信息
  const couponInfo: Ref<CouponActInfoWithGoodsAndState[]> = ref([]);
  // 展示位信息
  const imgUrlList: Ref<string[]> = ref([]);
  // 模块标题
  const title = ref('');
  // 背景图片
  const bgImg = ref('');
  // 左上角标签
  const tag = ref('');
  // tncUrl
  const tncUrl = ref();
  // ---- 变量end ----

  // ---- watch start ----
  watchEffect(() => {
    if (!merchantConfig.value.bankCouponList) {
      isHideModule.value = true;
      return;
    }
    // 初始化券
    setTimeout(() => {
      initBankAct(merchantConfig.value.bankCouponList);
    }, 0);
    // TODO: 这里不加setTimeout会报错，需要进一步定位
  });
  // ---- watch end ----
  // ---- 初始化start ----
  async function initBankAct(rawActIdList: BankActItemCfg[]) {
    console.log('initBankAct', rawActIdList);
    // 筛选配置
    const ableCfg = filterAbleCfg(rawActIdList);
    if (!ableCfg) {
      isHideModule.value = true;
      return;
    }
    title.value = ableCfg.title;
    bgImg.value = ableCfg.bgImg;
    tag.value = ableCfg.tag;
    tncUrl.value = ableCfg.tncUrl;
    if (ableCfg.actIdList?.length > 0) {
      // 手动领取券信息
      console.log('initBankAct cfg', ableCfg.actIdList.map(it => it.actIdEn));
      // 查券(初始化)
      const [err] = await to(createActivityObj(ableCfg.actIdList.map(it => decodeURIComponent(it.actIdEn))));
      if (err) {
        isHideModule.value = true;
        new CustomException(err, 'BankActInitFail', '银行券模块初始化失败');
        return;
      }
      // 获取券
      couponInfo.value = getCouponList();
      if (couponInfo.value.length === 0) {
        isHideModule.value = true;
        return;
      }
    }
    if (ableCfg.actImgList?.length > 0) {
      // 展示位信息
      imgUrlList.value = ableCfg.actImgList.map(item => item.imgUrl);
    }
    isReady.value = true;
    elk({
      key: 'bankActModuleBrow',
      str3: JSON.stringify(ableCfg.actIdList),
      str4: JSON.stringify(ableCfg.actImgList),
    });
  }
  // ---- 初始化end ----

  // 创建distribution对象并初始化
  let activityObj: DistributionActivity | null = null;
  async function createActivityObj(actIds: string[]) {
    activityObj = new DistributionActivity({
      couponActRelation: CouponActRelation.oneForOne,
      distributeActidList: actIds,
      isNeedUserCouponid: true,
      isNeedCouponGoodsInfo: true,
      couponStateSort: COUPON_SORT_STATE,
    });
    await activityObj.initAct();
  }
  // 查券
  async function updateCoupon() {
    await activityObj?.qryCoupon();

    couponInfo.value = getCouponList();
  }
  // 返回单张券
  function getCouponList(): CouponActInfoWithGoodsAndState[] {
    console.log('initBankAct couponList', activityObj?.couponList);
    return activityObj?.couponList ?? [];
  }
  // 领券
  async function receiveCoupon({ actListid, prizeId }: { actListid: string; prizeId: string}) {
    return await activityObj?.getCoupon({ actListid, prizeId });
  }

  return {
    isHideModule,
    isReady,
    title,
    bgImg,
    tag,
    tncUrl,
    couponInfo,
    imgUrlList,

    receiveCoupon,
    updateCoupon,
  };
}

/**
 * 从配置里提取有效的第1条配置
 * 两个条件：(1) startTime < 当前时间 < endTime (2) 券列表不为空
 */
function filterAbleCfg(cfg: BankActItemCfg[]): BankActItemCfg | undefined {
  const curTime = dayjs(getServerTime());
  const ableCfg = cfg.filter((item) => {
    const isTimeOk = curTime.isAfter(dayjs(item.startTime)) && curTime.isBefore(dayjs(item.endTime));
    const isActIdOk = item.actIdList?.length > 0 || item.actImgList?.length > 0;
    return isTimeOk && isActIdOk;
  });

  return ableCfg[0];  // 返回第1条数据（可能会为undefined）
}
